import { z } from 'zod';
import { normalizeString } from 'utils/Validations';

/**
 * Interfaz que define los valores del formulario para perfiles de turno
 */
export interface ShiftProfileFormValues {
    id?: number | string;
    name: string;
    description?: string;
    is_night: boolean;
    start_time_min: string;
    start_time_max: string;
    end_time_min: string;
    end_time_max: string;
    duration_standard: string;
    duration_min: string;
    duration_max: string;
}

const HH_MM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const buildTimeSchema = (t: (key: string, options?: any) => string) =>
    z
        .string()
        .trim()
        .min(1, t('general.zod.time_format_hm.required') as string)
        .refine((val) => !val.includes('_') && HH_MM_REGEX.test(val), {
            message: t('general.zod.time_format_hm.invalid') as string,
        });

export function createShiftProfileSchema(params: {
    t: (key: string, options?: any) => string;
    rows: any[];
    initialValues?: Partial<ShiftProfileFormValues>;
    readOnly?: boolean;
}) {
    const { t, rows, initialValues, readOnly = false } = params;
    const time = buildTimeSchema(t);

    return z
        .object({
            name: z
                .string()
                .trim()
                .min(3, t('general.zod.string.min_chars', { min: 3 }) as string)
                .max(100, t('general.zod.string.max_chars', { max: 100 }) as string),
            description: z
                .string()
                .trim()
                .max(255, t('general.zod.string.max_chars', { max: 255 }) as string)
                .optional()
                .or(z.literal('')),
            is_night: z.boolean().default(false),
            start_time_min: time,
            start_time_max: time,
            end_time_min: time,
            end_time_max: time,
            duration_standard: time,
            duration_min: time,
            duration_max: time,
        })
        .superRefine((data, ctx) => {
            const normalizedInput = normalizeString(data.name);
            const currentId = initialValues?.id;
            const existsInRows = rows?.some((r: any) => {
                const sameName = normalizeString(r?.shift_profile_name ?? r?.name ?? '') === normalizedInput;
                const isSameRecord = currentId !== undefined && r?.id === currentId;
                return sameName && !isSameRecord;
            });
            if (!readOnly && existsInRows) {
                ctx.addIssue({
                    path: ['name'],
                    code: 'custom',
                    message: t('general.zod.string.name_exist', { defaultValue: 'El nombre ya existe' }) as string,
                });
            }
        });
}
